import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AlertTriangle } from 'lucide-react';
import { Button, Select, Input } from '@/components/ui';
import { fetchTeams } from '@/store/slices/teamsSlice';
import type { AppDispatch, RootState } from '@/store';
import type { Resource, ResourceTeamAssignment } from '@/types';
import { format } from 'date-fns';

interface TransferResourceModalProps {
  resource: Resource;
  currentAssignment: ResourceTeamAssignment;
  onTransfer: (data: { newTeamId: string; transferDate?: string }) => void;
  onClose: () => void;
}

const TransferResourceModal: React.FC<TransferResourceModalProps> = ({
  resource,
  currentAssignment,
  onTransfer,
  onClose,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { teams } = useSelector((state: RootState) => state.teams);

  const [newTeamId, setNewTeamId] = useState('');
  const [transferDate, setTransferDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    dispatch(fetchTeams());
  }, [dispatch]);

  const availableTeams = teams.filter((t) => t.id !== currentAssignment.teamId);

  const teamOptions = availableTeams.map((t) => ({
    value: t.id,
    label: `${t.name} (${t.program?.name || 'No Program'})`,
  }));

  const selectedTeam = teams.find((t) => t.id === newTeamId);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!newTeamId) {
      newErrors.newTeamId = 'Please select a team';
    }

    if (!transferDate) {
      newErrors.transferDate = 'Transfer date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onTransfer({
        newTeamId,
        transferDate,
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Resource Info (Read-only) */}
      <div className="bg-surface-secondary p-4 rounded-lg space-y-2">
        <h4 className="text-sm font-semibold text-foreground">Resource</h4>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">Name:</span>
            <span className="ml-2 text-foreground font-medium">
              {resource.firstName} {resource.lastName}
            </span>
          </div>
          <div>
            <span className="text-muted-foreground">Type:</span>
            <span className="ml-2 text-foreground">
              {resource.resourceType.replace(/_/g, ' ')}
            </span>
          </div>
          <div>
            <span className="text-muted-foreground">Level:</span>
            <span className="ml-2 text-foreground">{resource.resourceLevel}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Resource ID:</span>
            <span className="ml-2 text-foreground font-mono">{resource.resourceId}</span>
          </div>
        </div>
      </div>

      {/* Current Team Info (Read-only) */}
      <div className="bg-surface-secondary p-4 rounded-lg space-y-2">
        <h4 className="text-sm font-semibold text-foreground">Current Team</h4>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">Team:</span>
            <span className="ml-2 text-foreground font-medium">
              {currentAssignment.team?.name}
            </span>
          </div>
          <div>
            <span className="text-muted-foreground">Program:</span>
            <span className="ml-2 text-foreground">
              {currentAssignment.team?.program?.name || 'No Program'}
            </span>
          </div>
          <div className="col-span-2">
            <span className="text-muted-foreground">Start Date:</span>
            <span className="ml-2 text-foreground">
              {format(new Date(currentAssignment.startDate), 'MMMM d, yyyy')}
            </span>
          </div>
        </div>
      </div>

      {/* New Team Selection */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-foreground">
          New Team <span className="text-status-error">*</span>
        </label>
        <Select
          value={newTeamId}
          onChange={(e) => {
            setNewTeamId(e.target.value);
            if (errors.newTeamId) {
              setErrors((prev) => ({ ...prev, newTeamId: '' }));
            }
          }}
          options={teamOptions}
          placeholder="Select a team"
          error={errors.newTeamId}
        />
      </div>

      {/* Transfer Date */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-foreground">
          Transfer Date
        </label>
        <Input
          type="date"
          value={transferDate}
          onChange={(e) => {
            setTransferDate(e.target.value);
            if (errors.transferDate) {
              setErrors((prev) => ({ ...prev, transferDate: '' }));
            }
          }}
          error={errors.transferDate}
        />
        <p className="text-xs text-muted-foreground">
          Defaults to today. This will be the end date of the current assignment and the start date of the new assignment.
        </p>
      </div>

      {/* Confirmation Message */}
      {selectedTeam && (
        <div className="bg-status-warning-bg border border-status-warning/30 rounded-lg p-4 flex gap-3">
          <AlertTriangle className="w-5 h-5 text-status-warning flex-shrink-0 mt-0.5" />
          <div className="text-sm text-status-warning">
            <p className="font-medium mb-1">Confirm Transfer</p>
            <p>
              This will end the current assignment on{' '}
              <span className="font-semibold">{format(new Date(transferDate), 'MMMM d, yyyy')}</span> and
              start a new assignment with{' '}
              <span className="font-semibold">{selectedTeam.name}</span> on the same date.
            </p>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabled={!newTeamId}>
          Transfer Resource
        </Button>
      </div>
    </form>
  );
};

export default TransferResourceModal;
